import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
  Button, 
  CircularProgress, 
  Container,
  Box,
  Typography,
  Checkbox,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterTypeDropdown from '../../components/Filters/FilterTypeDropdown';
import { normalizeFilterType, isFilterMultiSelect } from '../../utils/filterTypes';
import { formatBusinessText } from '../../utils/textFormatting';
import './FiltersPage.css';
import branding from '../../styles/branding';
import { useCustomAlert, useErrorToast } from '../../components/common';
import useApi from '../../hooks/useApi';
import { useProjectProgress, CHANGE_TYPES } from '../../contexts/ProjectProgressContext';
import Breadcrumb from '../../components/common/Breadcrumb';
import { getApiErrorMessage } from '../../utils/errorMessages';

const hasLocalUnsavedFilters = (columns, checked, existingFilters) => {
  const hasExisting = (col) => existingFilters.some(f => (f.columnName || f.ColumnName || f.filterName || f.FilterName) === col);
  return columns.some(col => checked[col] && !hasExisting(col));
};

function FiltersPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const { updateCurrentStep, markUnsavedChanges, trackChange, clearChangesTracker, clearChangesByType, registerSaveBeforeLeave, unregisterSaveBeforeLeave, currentProject, initializeProject } = useProjectProgress();
  const { showAlert } = useCustomAlert();
  const [columns, setColumns] = useState([]);
  const [checked, setChecked] = useState({});
  const [filterTypes, setFilterTypes] = useState({});
  const [existingFilters, setExistingFilters] = useState([]);
  const hasTrackedLocalFiltersRef = useRef(false);

  // When user clicks "Save" in the save progress dialog, persist selected filters to the database
  useEffect(() => {
    registerSaveBeforeLeave(async () => {
      const currentIdentifier = jobId;
      if (!currentIdentifier) return { success: true };

      const isValidUuid = (s) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
      let projectId = isValidUuid(currentIdentifier) ? currentIdentifier : null;
      if (!projectId) {
        try {
          const projectResponse = await api.get(`/projects`);
          if (projectResponse.data?.success && projectResponse.data.data?.length) {
            const matchingProject = projectResponse.data.data.find(p => {
              const pid = p.ProjectId ?? p.projectId;
              const fname = p.FileName ?? p.fileName;
              const pname = p.ProjectName ?? p.projectName;
              return pid === currentIdentifier || fname === currentIdentifier || pname === currentIdentifier;
            });
            if (matchingProject) projectId = matchingProject.ProjectId ?? matchingProject.projectId;
          }
        } catch (err) {
          console.warn('Could not resolve Project for save-before-leave:', err);
        }
      }
      if (!projectId) return { success: false, error: 'Could not determine project. Please try saving from the Filters page.' };

      const hasExisting = (col) => existingFilters.some(f => (f.columnName || f.ColumnName || f.filterName || f.FilterName) === col);
      const pendingNew = columns.filter(col => checked[col] && !hasExisting(col));
      if (pendingNew.length === 0) return { success: true };
      const missingType = pendingNew.filter(col => !filterTypes[col]);
      if (missingType.length > 0) {
        return {
          success: false,
          error: `Select a filter type for: ${missingType.join(', ')} before saving, or uncheck those columns.`,
        };
      }
      const newFilters = columns
        .filter(col => checked[col] && filterTypes[col] && !hasExisting(col))
        .map(col => ({
          ProjectId: projectId,
          ColumnName: col,
          FilterName: col,
          FilterType: filterTypes[col],
          IsMultiSelect: isFilterMultiSelect(filterTypes[col]) ? 1 : 0
        }));

      if (newFilters.length === 0) return { success: true };

      try {
        const response = await api.post('/filters', { filters: newFilters });
        if (response.data?.success) return { success: true };
        return { success: false, error: getApiErrorMessage(null, 'Failed to save filters.') };
      } catch (err) {
        const msg = getApiErrorMessage(err, 'Failed to save filters.');
        return { success: false, error: msg };
      }
    });
    return () => unregisterSaveBeforeLeave();
  }, [jobId, columns, checked, filterTypes, existingFilters, api, registerSaveBeforeLeave, unregisterSaveBeforeLeave]);
  
  // Rehydrate project from URL when on Filters with jobId but no context (refresh, direct link) so trackChange + save dialog work
  useEffect(() => {
    if (jobId && !currentProject) {
      initializeProject(
        { projectId: jobId, projectName: 'Project', currentStep: 'filter' },
        { markUnsaved: false }
      );
    }
  }, [jobId, currentProject, initializeProject]);

  // Update current step when component mounts
  useEffect(() => {
    updateCurrentStep('filter');
  }, [updateCurrentStep]);
  const [isLoadingColumns, setIsLoadingColumns] = useState(false);
  const [isAddingFilters, setIsAddingFilters] = useState(false);
  const [deletingFilterId, setDeletingFilterId] = useState(null);
  const [error, setError] = useState(null);
  const [viewValuesOpen, setViewValuesOpen] = useState(false);
  const [viewValuesColumn, setViewValuesColumn] = useState(null);
  const [viewValuesList, setViewValuesList] = useState([]);
  const [viewValuesLoading, setViewValuesLoading] = useState(false);
  const { showErrorToast } = useErrorToast();

  useEffect(() => {
    if (error) {
      showErrorToast(error);
      setError(null);
    }
  }, [error, showErrorToast]);
  const hasLoadedColumns = useRef(false);

  // Track unsaved changes when user has selected new filter row(s) not yet persisted via Add Filter / Save
  useEffect(() => {
    const hasLocal = columns.length > 0 && hasLocalUnsavedFilters(columns, checked, existingFilters);
    if (hasLocal && !hasTrackedLocalFiltersRef.current) {
      trackChange(CHANGE_TYPES.ADD_FILTER, 'Unsaved filter selection');
      hasTrackedLocalFiltersRef.current = true;
    }
    if (!hasLocal) {
      hasTrackedLocalFiltersRef.current = false;
      clearChangesByType(CHANGE_TYPES.ADD_FILTER);
    }
  }, [columns, checked, existingFilters, trackChange, clearChangesByType]);

  // Function to fetch existing filters for the current job
  const fetchExistingFilters = async (currentJobId) => {
    try {
      console.log('Fetching existing filters for jobId:', currentJobId);
      const response = await api.get(`/filters/${currentJobId}`);
      
      if (response.data?.success) {
        const existingFiltersData = response.data.data || [];
        console.log('Existing filters loaded:', existingFiltersData);
        setExistingFilters(existingFiltersData);
        
        // Pre-populate checked state and filter types for existing filters
        if (existingFiltersData && existingFiltersData.length > 0) {
          const newChecked = {};
          const newFilterTypes = {};
          
          existingFiltersData.forEach(filter => {
            const columnName = filter.columnName || filter.ColumnName || filter.filterName || filter.FilterName;
            if (columnName) {
              newChecked[columnName] = true;
              newFilterTypes[columnName] = normalizeFilterType(
                filter.filterType || filter.FilterType || ''
              );
            }
          });
          
          setChecked(prevChecked => ({ ...prevChecked, ...newChecked }));
          setFilterTypes(prevTypes => ({ ...prevTypes, ...newFilterTypes }));
          console.log('Pre-populated filters:', newChecked, newFilterTypes);
        }
      } else {
        console.log('No existing filters found or error fetching filters');
        setExistingFilters([]);
      }
    } catch (error) {
      console.error('Error fetching existing filters:', error);
      setExistingFilters([]);
    }
  };

  useEffect(() => {
    const fetchColumns = async () => {
      // Prevent duplicate calls
      if (isLoadingColumns || !jobId || hasLoadedColumns.current) {
        console.log(`Skipping columns fetch - loading: ${isLoadingColumns}, jobId: ${jobId}, hasLoaded: ${hasLoadedColumns.current}`);
        return;
      }
      
      hasLoadedColumns.current = true;
      setIsLoadingColumns(true);
      setError(null);
      
      try {
        console.log(`Fetching columns for jobId: ${jobId}`);
        const res = await api.get(`/jobs/${jobId}?getColumns=true`);
        
        if (res.data?.success) {
          const data = res.data.data || {};
          console.log('Full API response:', data);
          setColumns(data.columns || []);
          console.log(`Columns loaded: ${data.columns?.length || 0} columns`);
          
          // If no columns were returned, reset the flag to allow retry after a delay
          if (!data.columns || data.columns.length === 0) {
            console.log('No columns returned, will retry after 2 seconds');
            hasLoadedColumns.current = false;
            setTimeout(() => {
              if (!hasLoadedColumns.current) {
                console.log('Retrying columns fetch...');
                fetchColumns();
              }
            }, 2000);
          }
        } else {
          throw new Error(res.data?.error || 'Failed to fetch columns');
        }
      } catch (err) {
        console.error("Error fetching columns", err);
        setError(getApiErrorMessage(err, 'Failed to load columns'));
        hasLoadedColumns.current = false; // Reset on error to allow retry
        // Retry after 3 seconds on error
        setTimeout(() => {
          if (!hasLoadedColumns.current) {
            console.log('Retrying columns fetch after error...');
            fetchColumns();
          }
        }, 3000);
      } finally {
        setIsLoadingColumns(false);
      }
    };
    
    if (jobId) {
      fetchColumns();
      fetchExistingFilters(jobId);
    }
  }, [jobId, api]);

  // Helper to determine if filter type is multi-select
  const isMultiSelect = (type) => isFilterMultiSelect(type);

  // Helper to check if a column already has a filter
  const hasExistingFilter = (columnName) => {
    return existingFilters.some(filter => {
      const filterColumnName = filter.columnName || filter.ColumnName || filter.filterName || filter.FilterName;
      return filterColumnName === columnName;
    });
  };

  const getExistingFilter = (columnName) => {
    return existingFilters.find(filter => {
      const col = filter.columnName || filter.ColumnName || filter.filterName || filter.FilterName;
      return col === columnName;
    });
  };

  const handleDeleteFilter = async (col) => {
    const filterObj = getExistingFilter(col);
    const filterId = filterObj?.filterId || filterObj?.FilterId || filterObj?.FilterID;
    if (!filterId) return;
    setDeletingFilterId(filterId);
    try {
      const response = await api.delete('/filters', { data: { filterId } });
      if (response.data?.success !== false && (response.status === 200 || response.status === 204)) {
        setExistingFilters(prev => prev.filter(f => (f.filterId || f.FilterId || f.FilterID) !== filterId));
        setChecked(prev => ({ ...prev, [col]: false }));
        setFilterTypes(prev => ({ ...prev, [col]: '' }));
        // Delete is persisted to DB; no unsaved change to track
      } else {
        showErrorToast(getApiErrorMessage(null, 'Failed to delete filter. Please try again.'));
      }
    } catch (err) {
      console.error('Error deleting filter:', err);
      showErrorToast(getApiErrorMessage(err, 'Failed to delete filter.'));
    } finally {
      setDeletingFilterId(null);
    }
  };

  const handleCheckboxChange = (col) => {
    // Don't allow unchecking if it's an existing filter
    if (hasExistingFilter(col)) {
      return;
    }
    
    setChecked(prev => ({ ...prev, [col]: !prev[col] }));
    // Optionally reset filter type when unchecked
    if (checked[col]) {
      setFilterTypes(prev => ({ ...prev, [col]: '' }));
    }
  };

  const handleAddFilter = async () => {
    const currentIdentifier = jobId; // Can be projectId, fileName, or tableName
    
    if (!currentIdentifier) {
      showAlert('Project ID, File Name, or Table Name not found. Please navigate from the upload page.', { severity: 'warning' });
      return;
    }
    
    console.log('Using identifier from URL for filters:', currentIdentifier);
    
    // Resolve ProjectId: URL param is often projectId when coming from Projects; support projects list match too
    const isValidUuid = (s) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
    let projectId = isValidUuid(currentIdentifier) ? currentIdentifier : null;
    if (!projectId) {
      try {
        const projectResponse = await api.get(`/projects`);
        if (projectResponse.data?.success) {
          const projects = projectResponse.data.data || [];
          const matchingProject = projects.find(p => {
            const pid = p.ProjectId ?? p.projectId;
            const fname = p.FileName ?? p.fileName;
            const pname = p.ProjectName ?? p.projectName;
            return pid === currentIdentifier || fname === currentIdentifier || pname === currentIdentifier;
          });
          if (matchingProject) {
            projectId = matchingProject.ProjectId ?? matchingProject.projectId;
          }
        }
      } catch (error) {
        console.warn('Could not resolve Project from list:', error);
      }
    }
    if (!projectId) {
      showAlert('Could not determine Project. Please open Filters from a Project (Projects list) so the correct Project ID is used.', { severity: 'warning' });
      return;
    }
    
    // Check for duplicate filters before processing
    const duplicateFilters = [];
    const newFilters = [];
    
    columns
      .filter(col => checked[col] && filterTypes[col])
      .forEach(col => {
        if (hasExistingFilter(col)) {
          duplicateFilters.push(col);
        } else {
          newFilters.push({
            ProjectId: projectId,
            ColumnName: col,
            FilterName: col,
            FilterType: filterTypes[col],
            IsMultiSelect: isMultiSelect(filterTypes[col]) ? 1 : 0
          });
        }
      });

    // Only block when ALL selected filters already exist in the database for this project
    if (newFilters.length === 0) {
      if (duplicateFilters.length > 0) {
        showAlert(`The following filters already exist and cannot be added again: ${duplicateFilters.join(', ')}`, { severity: 'warning' });
      } else {
        showAlert('Please select at least one column and filter type.', { severity: 'warning' });
      }
      return;
    }

    setIsAddingFilters(true);
    try {
      console.log('Calling filters API with:', newFilters);
      const response = await api.post('/filters', { 
        filters: newFilters 
      });
      
      console.log('Filters API response:', response.data);
      
      if (response.data?.success) {
        const result = response.data.data || {};
        console.log('Filters created successfully:', result);
        
        // Clear selections after successful add (persisted to DB; clear unsaved state)
        setChecked({});
        setFilterTypes({});
        await fetchExistingFilters(currentIdentifier);
        clearChangesTracker();
        hasTrackedLocalFiltersRef.current = false;
        const createdCount = result.filterIds?.length || 0;
        const successMsg = duplicateFilters.length > 0
          ? `Filters added successfully! Created ${createdCount} new filter(s). The following were not added because they already exist: ${duplicateFilters.join(', ')}`
          : `Filters added successfully! Created ${createdCount} filters.`;
        showAlert(successMsg, { severity: 'success' });
        navigate(`/cards/${jobId}`);
      } else {
        throw new Error('Failed to create filters');
      }
    } catch (err) {
      console.error("Error adding filters:", err);
      showErrorToast(getApiErrorMessage(err, 'Failed to add filters.'));
    } finally {
      setIsAddingFilters(false);
    }
  };

  const handleUpload = () => {
    navigate('/landing');
  };

  const handleViewValues = async (col) => {
    if (!jobId || !col) return;
    setViewValuesColumn(col);
    setViewValuesOpen(true);
    setViewValuesList([]);
    setViewValuesLoading(true);
    try {
      const res = await api.get(`/filters/${jobId}`, { params: { column: col } });
      const data = res.data?.data ?? res.data;
      const values = Array.isArray(data?.values) ? data.values : [];
      setViewValuesList(values);
    } catch (err) {
      console.warn('Failed to load column values:', err);
      setViewValuesList([]);
    } finally {
      setViewValuesLoading(false);
    }
  };

  const handleCloseViewValues = () => {
    setViewValuesOpen(false);
    setViewValuesColumn(null);
    setViewValuesList([]);
  };

  return (
    <>
      <Breadcrumb currentStep="filters" />
      <Container maxWidth="xl" sx={{ py: 2 }}>
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 2,
            fontFamily: branding.fonts.primary,
            fontWeight: 600,
            fontSize: '22px'
          }}
        >
          Choose Filters
        </Typography>


        {isLoadingColumns && (
          <Box sx={{ textAlign: 'center', padding: '20px', color: '#666', fontSize: '12px' }}>
            <CircularProgress size={24} sx={{ mr: 1, verticalAlign: 'middle' }} />
            Loading columns... Please wait.
          </Box>
        )}

        <TableContainer component={Paper} className="survey-table" sx={{ border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden', mb: 2.5 }}>
          <Table size="small" aria-label="Choose filters">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#DCEFF2' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '12px', padding: '10px 16px', borderBottom: '1px solid #ddd' }}>Select</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '12px', padding: '10px 16px', borderBottom: '1px solid #ddd' }}>Column Name</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '12px', padding: '10px 16px', borderBottom: '1px solid #ddd', pl: 4 }}>Filter Type</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, fontSize: '12px', padding: '10px 16px', borderBottom: '1px solid #ddd', width: 100 }}>View Values</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '12px', padding: '10px 16px', borderBottom: '1px solid #ddd', width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {columns.length > 0 ? (
                columns.map(col => {
                  const hasExisting = hasExistingFilter(col);
                  const existingFilter = getExistingFilter(col);
                  const rowFilterId = existingFilter?.filterId || existingFilter?.FilterId || existingFilter?.FilterID;
                  return (
                    <TableRow
                      key={col}
                      sx={{
                        opacity: hasExisting ? 0.8 : 1,
                        backgroundColor: hasExisting ? '#e8f5e9' : 'inherit',
                        borderLeft: hasExisting ? '1px solid #4caf50' : 'none',
                        borderRight: hasExisting ? '1px solid #4caf50' : 'none',
                        '&:last-child td': { borderBottom: hasExisting ? '1px solid #4caf50' : '1px solid #ddd' },
                        '& td': { borderBottom: '1px solid #ddd', fontSize: '12px', padding: '10px 16px' }
                      }}
                    >
                      <TableCell sx={{ borderBottom: '1px solid #ddd' }}>
                        <Checkbox
                          checked={!!checked[col]}
                          onChange={() => handleCheckboxChange(col)}
                          disabled={hasExisting}
                          size="small"
                          sx={{ 
                            padding: '4px',
                            '&.Mui-disabled': { color: '#4caf50' }
                          }}
                          title={hasExisting ? `Filter already saved for "${col}"` : ''}
                        />
                      </TableCell>
                      <TableCell sx={{ borderBottom: '1px solid #ddd', color: hasExisting ? '#2e7d32' : 'inherit', fontWeight: hasExisting ? 500 : 'normal' }}>
                        {formatBusinessText(col)}
                        {hasExisting && (
                          <span style={{ color: '#4caf50', marginLeft: '8px', fontSize: '10px' }}>✓ Saved</span>
                        )}
                      </TableCell>
                      <TableCell sx={{ borderBottom: '1px solid #ddd', pl: 4, opacity: checked[col] ? 1 : 0.5, pointerEvents: hasExisting ? 'none' : (checked[col] ? 'auto' : 'none'), bgcolor: checked[col] ? 'inherit' : '#f5f5f5' }}>
                        <FilterTypeDropdown
                          value={filterTypes[col] || ''}
                          onChange={val => setFilterTypes(prev => ({ ...prev, [col]: val }))}
                          disabled={!checked[col] || hasExisting}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ borderBottom: '1px solid #ddd', width: 100 }}>
                        <Tooltip title="View distinct values for this column">
                          <IconButton
                            size="small"
                            onClick={() => handleViewValues(col)}
                            sx={{ color: '#000000' }}
                            aria-label={`View values for ${col}`}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ borderBottom: '1px solid #ddd', width: 48 }}>
                        {hasExisting && (
                          <Tooltip title="Remove this filter from the project">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteFilter(col)}
                              disabled={deletingFilterId !== null}
                              sx={{ color: '#000000' }}
                              aria-label={`Delete filter for ${col}`}
                            >
                              {deletingFilterId === rowFilterId ? (
                                <CircularProgress size={20} />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : null}
            </TableBody>
          </Table>
          {!isLoadingColumns && columns.length === 0 && (
            <Box sx={{ textAlign: 'center', padding: '20px', color: '#666', fontSize: '12px' }}>
              No columns found. Please check if the data was uploaded correctly.
            </Box>
          )}
        </TableContainer>

        <div className="button-group">
          <Button 
            variant="contained"
            onClick={handleAddFilter}
            disabled={isAddingFilters}
            startIcon={isAddingFilters ? <CircularProgress size={20} /> : null}
            sx={{
              fontFamily: branding.fonts.primary,
              fontSize: '12px',
              backgroundColor: '#000000',
              '&:hover': { backgroundColor: '#333333' },
              '&:disabled': { backgroundColor: '#d3d3d3', color: '#a9a9a9' }
            }}
          >
            {isAddingFilters ? 'Adding Filters...' : 'Add Filter'}
          </Button>
          <Button 
            component={Link}
            to={`/cards/${jobId}`}
            variant="outlined"
            sx={{
              fontFamily: branding.fonts.primary,
              fontSize: '12px',
              borderColor: '#000000',
              color: '#000000',
              '&:hover': { borderColor: '#333333', backgroundColor: 'rgba(0,0,0,0.04)' }
            }}
          >
            Skip
          </Button>
        </div>

        <Dialog
          open={viewValuesOpen}
          onClose={handleCloseViewValues}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { fontFamily: branding.fonts.primary } }}
        >
          <DialogTitle>
            Distinct values {viewValuesColumn ? `for "${formatBusinessText(viewValuesColumn)}"` : ''}
          </DialogTitle>
          <DialogContent>
            {viewValuesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={32} />
              </Box>
            ) : viewValuesList.length === 0 ? (
              <Typography color="text.secondary">No values found for this column.</Typography>
            ) : (
              <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
                {viewValuesList.map((val, idx) => (
                  <ListItem key={idx}>
                    <ListItemText primary={val} />
                  </ListItem>
                ))}
              </List>
            )}
            {!viewValuesLoading && viewValuesList.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {viewValuesList.length} distinct value(s) — these are the options that will appear in the dashboard filter.
              </Typography>
            )}
          </DialogContent>
        </Dialog>
      </Container>
    </>
  );
}

export default FiltersPage;
